import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useHotkeys } from 'react-hotkeys-hook';

import { cn } from '../../lib/utils';

import { useRegisterWithKeyMaster } from '../../contexts/KeyMaster';
import { useListNavigation } from '../../hooks/useListNavigation';
import { GlassWrapper } from './GlassWrapper';

/**
 * A reusable dropdown menu with keyboard navigation.
 *
 * @param {Object} props
 * @param {Array} props.items - Array of { value, label, selected? } objects
 * @param {Function} props.onSelect - Called with the item's value when selected
 * @param {Function} props.onClose - Called when the menu should close (Escape key)
 * @param {Symbol} props.keychainId - KeyMaster keychain ID for keyboard priority
 * @param {string} props.className - Additional class names for the wrapper
 * @param {Function} props.renderItem - Optional custom render function for items
 */
export function DropdownMenu({ items, onSelect, onClose, keychainId, className, renderItem }) {
  const scrollContainerRef = useRef(null);
  const itemRefs = useRef({});

  const { unlocked } = useRegisterWithKeyMaster(keychainId);

  // Find initial index from selected item
  const initialIndex = useMemo(
    function computeInitialIndex() {
      const idx = items.findIndex(function isSelected(item) {
        return item.selected;
      });
      return idx >= 0 ? idx : 0;
    },
    [items],
  );

  const handleSelect = useCallback(
    function handleSelectCallback(item) {
      onSelect(item.value);
    },
    [onSelect],
  );

  const { selectedIndex, isKeyboardNavRef, createMouseEnterHandler, createClickHandler, onMouseLeave, onKeyDown } = useListNavigation({
    items,
    onSelect: handleSelect,
    wrap: true,
    initialIndex,
  });

  // Scroll to initially selected item on mount
  const hasScrolledToInitial = useRef(false);
  useEffect(() => {
    if (hasScrolledToInitial.current || initialIndex < 0) return;
    const el = itemRefs.current[initialIndex];
    if (el && scrollContainerRef.current) {
      hasScrolledToInitial.current = true;
      el.scrollIntoView({ behavior: 'instant', block: 'nearest' });
    }
  }, [initialIndex]);

  // Only scroll on keyboard nav, mouse hover was causing unwanted scrolls on edge rows
  useEffect(() => {
    if (!isKeyboardNavRef.current) return;
    isKeyboardNavRef.current = false;
    if (selectedIndex >= 0 && selectedIndex < items.length) {
      const activeElement = itemRefs.current[selectedIndex];
      if (activeElement && scrollContainerRef.current) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedIndex, items.length, isKeyboardNavRef]);

  const handleNavigationKeys = useCallback(
    function handleNavigationKeysCallback(event) {
      if (!unlocked()) return;
      onKeyDown({ event });
    },
    [unlocked, onKeyDown],
  );

  const handleEscapeKey = useCallback(
    function handleEscapeKeyCallback() {
      if (!unlocked()) return;
      if (onClose) {
        onClose();
      }
    },
    [onClose, unlocked],
  );

  // Forward hotkeys to useListNavigation (with unlocked check)
  useHotkeys('up,down,enter,space', handleNavigationKeys, { preventDefault: true }, [handleNavigationKeys]);

  // Handle escape separately for closing
  useHotkeys('esc', handleEscapeKey, { preventDefault: true }, [handleEscapeKey]);

  const createRefHandler = useCallback(function createRefHandlerCallback(index) {
    return function setRef(el) {
      itemRefs.current[index] = el;
    };
  }, []);

  return (
    <GlassWrapper variant="agent-chat-dropdown" ref={scrollContainerRef} className={className}>
      <div className="flex w-full flex-col gap-1 p-2" onMouseLeave={onMouseLeave}>
        {items.map(function renderDropdownItem(item, index) {
          const isActive = index === selectedIndex;
          const isSelected = item.selected;

          if (renderItem) {
            return renderItem({
              item,
              index,
              isActive,
              isSelected,
              ref: createRefHandler(index),
              onClick: createClickHandler(index),
              onMouseMove: createMouseEnterHandler(index),
            });
          }

          return (
            <button
              key={item.value}
              ref={createRefHandler(index)}
              className={cn(
                'flex w-full cursor-pointer items-center gap-1.5 rounded-xl px-2 py-1.5 text-left text-xs/5',
                isSelected && 'font-semibold',
                isActive
                  ? 'bg-gray-300/40 text-black dark:bg-neutral-700/50 dark:text-white'
                  : isSelected
                    ? 'text-black dark:text-white'
                    : 'text-gray-500 hover:bg-gray-300/40 dark:text-neutral-400 dark:hover:bg-neutral-700/50',
              )}
              onClick={createClickHandler(index)}
              onMouseMove={createMouseEnterHandler(index)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </GlassWrapper>
  );
}
